import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

function formatError(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function LoginPage() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Login
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");

  // Register
  const [regName, setRegName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [regMode, setRegMode] = useState("owner"); // "owner" or "staff"
  const [restaurantName, setRestaurantName] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [staffRole, setStaffRole] = useState("host");

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(loginEmail, loginPassword);
      navigate("/");
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");
    if (regPassword.length < 6) { setError("Password must be at least 6 characters"); return; }
    setSubmitting(true);
    try {
      const role = regMode === "owner" ? "admin" : staffRole;
      await register(regEmail, regPassword, regName, role, restaurantName, inviteCode);
      navigate("/");
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: "#F9F9F7" }}>
      <div className="w-full max-w-md animate-fade-in" data-testid="login-page">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold tracking-tight" style={{ color: "#2F4F4F", fontFamily: "Work Sans" }}>
            Dining Floor
          </h1>
          <p className="text-stone-500 mt-2 text-sm">Restaurant management made simple</p>
          <div className="flex items-center justify-center gap-3 mt-3">
            <Link to="/privacy" className="text-xs text-stone-400 hover:text-stone-600 underline">Privacy Policy</Link>
            <span className="text-stone-300">|</span>
            <Link to="/terms" className="text-xs text-stone-400 hover:text-stone-600 underline">Terms of Service</Link>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-stone-200 shadow-sm p-6">
          <Tabs defaultValue="login">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="login" data-testid="login-tab">Sign In</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">Get Started</TabsTrigger>
            </TabsList>
            {error && (
              <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="auth-error">
                {error}
              </div>
            )}
            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="login-email" className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Email</Label>
                  <Input id="login-email" type="email" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} placeholder="admin@restaurant.com" required data-testid="login-email-input" className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="login-password" className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Password</Label>
                  <Input id="login-password" type="password" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} placeholder="Enter password" required data-testid="login-password-input" className="mt-1.5" />
                </div>
                <Button type="submit" disabled={submitting} className="w-full" style={{ backgroundColor: "#2F4F4F" }} data-testid="login-submit-btn">
                  {submitting ? "Signing in..." : "Sign In"}
                </Button>
              </form>
            </TabsContent>
            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4">
                {/* Choose: Owner or Staff */}
                <div className="flex gap-2">
                  <button type="button" onClick={() => setRegMode("owner")}
                    className={`flex-1 p-3 rounded-lg border text-left transition-all ${regMode === "owner" ? "border-[#2F4F4F] bg-[#2F4F4F]/5" : "border-stone-200 hover:border-stone-300"}`}
                    data-testid="reg-mode-owner"
                  >
                    <p className="text-sm font-semibold" style={{ color: regMode === "owner" ? "#2F4F4F" : "#78716C" }}>Restaurant Owner</p>
                    <p className="text-[10px] text-stone-400 mt-0.5">Create a new restaurant</p>
                  </button>
                  <button type="button" onClick={() => setRegMode("staff")}
                    className={`flex-1 p-3 rounded-lg border text-left transition-all ${regMode === "staff" ? "border-[#2F4F4F] bg-[#2F4F4F]/5" : "border-stone-200 hover:border-stone-300"}`}
                    data-testid="reg-mode-staff"
                  >
                    <p className="text-sm font-semibold" style={{ color: regMode === "staff" ? "#2F4F4F" : "#78716C" }}>Staff Member</p>
                    <p className="text-[10px] text-stone-400 mt-0.5">Join with invite code</p>
                  </button>
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Your Name</Label>
                  <Input value={regName} onChange={(e) => setRegName(e.target.value)} placeholder="Full name" required data-testid="register-name-input" className="mt-1.5" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Email</Label>
                  <Input type="email" value={regEmail} onChange={(e) => setRegEmail(e.target.value)} placeholder="email@example.com" required data-testid="register-email-input" className="mt-1.5" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Password</Label>
                  <Input type="password" value={regPassword} onChange={(e) => setRegPassword(e.target.value)} placeholder="Min 6 characters" required data-testid="register-password-input" className="mt-1.5" />
                </div>

                {regMode === "owner" ? (
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Restaurant Name</Label>
                    <Input value={restaurantName} onChange={(e) => setRestaurantName(e.target.value)} placeholder="e.g., The Blue Table" required data-testid="register-restaurant-input" className="mt-1.5" />
                  </div>
                ) : (
                  <>
                    <div>
                      <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Invite Code</Label>
                      <Input value={inviteCode} onChange={(e) => setInviteCode(e.target.value.toUpperCase())} placeholder="e.g., A3F8B2C1" required data-testid="register-invite-input" className="mt-1.5 font-mono tracking-widest" maxLength={8} />
                      <p className="text-[10px] text-stone-400 mt-1">Get this from your restaurant manager</p>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Role</Label>
                      <div className="flex gap-2 mt-1.5">
                        <button type="button" onClick={() => setStaffRole("host")}
                          className={`flex-1 py-2 rounded-md text-xs font-medium border transition-all ${staffRole === "host" ? "border-[#2F4F4F] bg-[#2F4F4F] text-white" : "border-stone-200 text-stone-600"}`}
                          data-testid="reg-role-host"
                        >
                          Host / Hostess
                        </button>
                        <button type="button" onClick={() => setStaffRole("server")}
                          className={`flex-1 py-2 rounded-md text-xs font-medium border transition-all ${staffRole === "server" ? "border-[#2F4F4F] bg-[#2F4F4F] text-white" : "border-stone-200 text-stone-600"}`}
                          data-testid="reg-role-server"
                        >
                          Server
                        </button>
                      </div>
                    </div>
                  </>
                )}

                <Button type="submit" disabled={submitting} className="w-full" style={{ backgroundColor: "#2F4F4F" }} data-testid="register-submit-btn">
                  {submitting ? "Setting up..." : regMode === "owner" ? "Create Restaurant" : "Join Restaurant"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>
        <p className="text-xs text-stone-400 text-center mt-4">Created by Jordan Thomas</p>
      </div>
    </div>
  );
}
